import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '../../app/layouts/AuthLayout.js';
import { currentLanguage } from '../../lib/i18n/index.js';
import { PageHeader } from '../../ui/components/PageHeader.js';
import { afterSignIn, consumeCode } from './api.js';
import { clearAttempt, loadAttempt } from './attempt.js';
import { errorCode, SignedIn } from './parts.js';
import { timeZoneField } from './SignInPage.js';
import '../../ui/components/button.css';
import '../../ui/components/field.css';
import './sign-in.css';

type State =
  | { kind: 'checking' }
  | { kind: 'signed-in'; destination: string; otherDevice: boolean }
  | { kind: 'failed'; message: string };

/** Each link is used once per page load, even when React mounts the page twice (StrictMode). */
const consumed = new Map<string, Promise<Awaited<ReturnType<typeof consumeCode>>>>();

/** The link's attempt and code, read once and then removed from the address bar and history. */
function takeLink(): { preAuthSessionId: string; linkCode: string } | undefined {
  const preAuthSessionId = new URLSearchParams(window.location.search).get('preAuthSessionId');
  const linkCode = decodeURIComponent(window.location.hash.replace(/^#/, ''));
  if (preAuthSessionId && linkCode) {
    window.history.replaceState(window.history.state, '', '/auth/verify');
    return { preAuthSessionId, linkCode };
  }
  return undefined;
}

let taken: { link: ReturnType<typeof takeLink> } | undefined;

/** The link, taken from the address bar the first time it is asked for. */
function linkOnce(): ReturnType<typeof takeLink> {
  taken ??= { link: takeLink() };
  return taken.link;
}

/**
 * The magic-link landing page (`/auth/verify?preAuthSessionId=…#linkCode`).
 * It removes the link from the address bar and history straight away, signs
 * in, and moves on with a view transition. Opened on another device or
 * browser than the one that asked for it, the link still works, and the page
 * says so plainly ("you're signed in on this device").
 */
export function VerifyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<State>(() =>
    linkOnce() ? { kind: 'checking' } : { kind: 'failed', message: t('verify.invalid') },
  );

  useEffect(() => {
    const link = linkOnce();
    if (!link) return;
    let attempt = consumed.get(link.linkCode);
    if (!attempt) {
      attempt = consumeCode({ ...link, locale: currentLanguage(), ...timeZoneField() });
      consumed.set(link.linkCode, attempt);
    }
    let active = true;
    attempt.then(
      async (result) => {
        if (!active) return;
        if (result.status !== 'SIGNED_IN') {
          setState({ kind: 'failed', message: t('verify.used') });
          return;
        }
        const saved = loadAttempt();
        const sameDevice = saved?.preAuthSessionId === link.preAuthSessionId;
        clearAttempt();
        const destination = await afterSignIn(result.account, saved?.next);
        setState({ kind: 'signed-in', destination, otherDevice: !sameDevice });
        if (sameDevice) {
          window.setTimeout(() => {
            void navigate({ to: destination, viewTransition: true });
          }, 650);
        }
      },
      (failure: unknown) => {
        if (!active) return;
        const code = errorCode(failure, ['account_unavailable', 'rate_limited']);
        setState({ kind: 'failed', message: t(`signIn.errors.${code}`) });
      },
    );
    return () => {
      active = false;
    };
  }, [navigate, t]);

  return (
    <AuthLayout>
      <div className="sign-in">
        {state.kind === 'signed-in' ? null : <PageHeader title={t('signIn.title')} />}
        {state.kind === 'checking' ? (
          <p className="sign-in__status" role="status">
            {t('verify.checking')}
          </p>
        ) : state.kind === 'signed-in' ? (
          <>
            <SignedIn
              title={state.otherDevice ? t('verify.otherDevice.title') : t('signIn.done.title')}
              body={state.otherDevice ? t('verify.otherDevice.body') : t('signIn.done.body')}
            />
            {state.otherDevice ? (
              <button
                className="btn btn--primary sign-in__submit"
                type="button"
                onClick={() => void navigate({ to: state.destination, viewTransition: true })}
              >
                {t('verify.continue')}
              </button>
            ) : null}
          </>
        ) : (
          <div className="sign-in__form">
            <h2 className="sign-in__step-title">{t('verify.failedTitle')}</h2>
            <p role="alert">{state.message}</p>
            <a className="btn btn--primary sign-in__submit" href="/sign-in">
              {t('verify.signInAgain')}
            </a>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
